import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { privateAPI } from '../util/api';

const EditWebhook = () => {
  const { webhookId } = useParams();
  const navigate = useNavigate();
  const [webhook, setWebhook] = useState(null);
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false); // Для управления модальным окном

  useEffect(() => {
    const fetchWebhook = async () => {
      try {
        const response = await privateAPI.get(`/webhook/info/${webhookId}`);
        const fetchedWebhook = response.data;

        setWebhook(fetchedWebhook);

        if (fetchedWebhook.service === 'telegram') {
          const telegramResponse = await privateAPI.get(`/webhook/telegram/${webhookId}`);
          setBotToken(telegramResponse.data.bot_token);
          setChatId(telegramResponse.data.chat_id);
        } else if (fetchedWebhook.service === 'bybit') {
          const bybitResponse = await privateAPI.get(`/webhook/bybit/${webhookId}`);
          setApiKey(bybitResponse.data.api_key);
          setApiSecret(bybitResponse.data.api_secret);
        }
      } catch (error) {
        setError('Не удалось получить данные вебхука.');
      }
    };

    fetchWebhook();
  }, [webhookId]);

  const handleDeleteWebhook = async () => {
    try {
      await privateAPI.delete(`/webhook`, { data: { id: webhookId } });
      navigate('/dashboard');
    } catch (error) {
      setError('Не удалось удалить вебхук. Попробуйте еще раз.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const payload = {
        webhook_id: webhook._id,
        service: webhook.service,
        ...(webhook.service === 'telegram' && {
          bot_token: botToken,
          chat_id: chatId,
        }),
        ...(webhook.service === 'bybit' && {
          api_key: apiKey,
          api_secret: apiSecret,
        }),
      };

      const response = await privateAPI.put(`/webhook/${webhook.service}/${webhookId}`, payload);

      if (response.status === 200) {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('Произошла ошибка при обновлении вебхука.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex justify-center items-start mt-12">
      <div className="w-full max-w-lg p-6 bg-white shadow-md rounded-md">
        <h1 className="text-2xl font-semibold mb-4 text-center">Edit Webhook</h1>

        {error && <div className="text-red-500 mb-4 text-center">{error}</div>}

        {webhook && (
          <form onSubmit={handleSubmit} className={`${isSubmitting && 'opacity-50 pointer-events-none'}`}>
            <div className="mb-4">
              <label className="block text-gray-700">Webhook ID</label>
              <input
                type="text"
                className="w-full mt-2 p-2 border rounded-md"
                value={webhook._id}
                readOnly
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700">Webhook Name</label>
              <input
                type="text"
                className="w-full mt-2 p-2 border rounded-md"
                value={webhook.name}
                readOnly
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700">Service</label>
              <input
                type="text"
                className="w-full mt-2 p-2 border rounded-md"
                value={webhook.service.charAt(0).toUpperCase() + webhook.service.slice(1)}
                readOnly
              />
            </div>

            {webhook.service === 'telegram' && (
              <>
                <div className="mb-4">
                  <label className="block text-gray-700">Bot Token</label>
                  <input
                    type="text"
                    className="w-full mt-2 p-2 border rounded-md"
                    value={botToken}
                    onChange={(e) => setBotToken(e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700">Chat ID</label>
                  <input
                    type="text"
                    className="w-full mt-2 p-2 border rounded-md"
                    value={chatId}
                    onChange={(e) => setChatId(e.target.value)}
                  />
                </div>
              </>
            )}

            {webhook.service === 'bybit' && (
              <>
                <div className="mb-4">
                  <label className="block text-gray-700">API Key</label>
                  <input
                    type="text"
                    className="w-full mt-2 p-2 border rounded-md"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                  />
                </div>
                <div className="mb-4 relative">
                  <label className="block text-gray-700">API Secret</label>
                  <input
                    type={showApiSecret ? 'text' : 'password'}
                    className="w-full mt-2 p-2 border rounded-md"
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-10 text-sm text-blue-600"
                    onClick={() => setShowApiSecret(!showApiSecret)}
                  >
                    {showApiSecret ? 'Hide' : 'Show'}
                  </button>
                </div>
              </>
            )}

            <div className="flex justify-center">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-500 w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}

        <button
          type="button"
          className="bg-red-500 text-white px-4 py-2 mt-6 rounded-md hover:bg-red-600 w-full"
          onClick={() => setShowModal(true)}
        >
          Delete Webhook
        </button>

        {/* Модальное окно подтверждения удаления */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-semibold mb-4">Are you sure?</h2>
              <p className="mb-4">Deleting this webhook will also remove all associated logs.</p>
              <div className="flex justify-end gap-2">
                <button
                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-400"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-400"
                  onClick={() => {
                    setShowModal(false);
                    handleDeleteWebhook();
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditWebhook;